import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TREATMENTS } from "@/constants/testIds";

/** Editor de zonas de aplicacion + producto principal del inventario,
 * compartido entre TreatmentModal y TouchUpModal. La suma de `units` de
 * todas las filas se descuenta automaticamente del `primaryProductId`
 * elegido, en la MISMA unidad de medida de ese producto (unidades, ml, etc). */
export default function ZoneRowsEditor({
  zones, setZones, primaryProductId, setPrimaryProductId, products, zoneCatalog, suggestedZoneNames,
}) {
  const primaryProduct = products.find((p) => p.id === primaryProductId);
  const unitLabel = primaryProduct?.unit || "unidades";
  const totalUnits = zones.reduce((sum, z) => sum + (Number(z.units) || 0), 0);

  const addRow = (zoneName = "") => {
    setZones((rows) => [...rows, { zone_name: zoneName, units: "", notes: "" }]);
  };

  const updateRow = (index, patch) => {
    setZones((rows) => rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  const removeRow = (index) => {
    setZones((rows) => rows.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-sm text-muted-foreground mb-1 block">Producto principal a descontar del inventario</Label>
        <Select value={primaryProductId || "none"} onValueChange={(v) => setPrimaryProductId(v === "none" ? "" : v)}>
          <SelectTrigger data-testid={TREATMENTS.primaryProductSelect}><SelectValue placeholder="Ninguno" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Ninguno</SelectItem>
            {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.unit}, stock: {p.stock_quantity})</SelectItem>)}
          </SelectContent>
        </Select>
        {primaryProductId && (
          <p className="text-xs text-muted-foreground mt-1" data-testid={TREATMENTS.primaryProductUnitHint}>
            Ingresá las cantidades por zona en <strong>{unitLabel}</strong>, la misma unidad de este producto.
          </p>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <Label className="text-sm text-muted-foreground block">Zonas de aplicación</Label>
          <Button type="button" variant="ghost" size="sm" onClick={() => addRow()} data-testid={TREATMENTS.zoneAddButton}>
            <Plus className="w-3.5 h-3.5" /> Agregar zona
          </Button>
        </div>

        {suggestedZoneNames?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {suggestedZoneNames.map((name) => (
              <button
                key={name}
                type="button"
                className="text-[11px] px-2 py-0.5 rounded-full border border-border text-muted-foreground hover:bg-brand-light hover:text-brand hover:border-brand/40 transition-colors"
                onClick={() => addRow(name)}
              >
                + {name}
              </button>
            ))}
          </div>
        )}

        <datalist id="zone-catalog-options">
          {zoneCatalog.map((z) => <option key={z.id} value={z.name} />)}
        </datalist>

        {zones.length === 0 ? (
          <p className="text-sm text-muted-foreground">Sin zonas agregadas.</p>
        ) : (
          <div className="space-y-2">
            {zones.map((row, idx) => (
              <div key={idx} className="flex items-start gap-2" data-testid={`${TREATMENTS.zoneRowPrefix}${idx}`}>
                <Input
                  list="zone-catalog-options"
                  value={row.zone_name}
                  onChange={(e) => updateRow(idx, { zone_name: e.target.value })}
                  placeholder="Ej: Frente"
                  className="flex-[2]"
                  data-testid={`${TREATMENTS.zoneNameSelectPrefix}${idx}`}
                />
                <Input
                  type="number" min="0" step="0.01" value={row.units}
                  onChange={(e) => updateRow(idx, { units: e.target.value })}
                  placeholder={unitLabel}
                  className="w-24"
                  data-testid={`${TREATMENTS.zoneUnitsInputPrefix}${idx}`}
                />
                <Button type="button" variant="ghost" size="icon" onClick={() => removeRow(idx)} data-testid={`${TREATMENTS.zoneRemoveButtonPrefix}${idx}`}>
                  <X className="w-4 h-4 text-destructive" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {zones.length > 0 && (
          <p className="text-sm text-foreground font-medium mt-2" data-testid={TREATMENTS.zoneTotalUnits}>
            Total aplicado: {totalUnits} {unitLabel}
          </p>
        )}
      </div>
    </div>
  );
}
